import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { IArticle, PostAuthor } from '@bridebook/toolbox/src/schemas/article/types';
import { A, Box, ButtonV2, Container } from '@bridebook/ui';
import AuthorRelatedArticles from '@bridebook/web/app-shared/components/author-related-articles/author-related-articles';
import SignupLink from '@bridebook/web/app-shared/components/signup-link/signup-link';
import { getAuthorNetworks } from '@bridebook/web/app-shared/lib/articles/constants';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import Network from '@bridebook/web/components/generic/social-sharing/network';
import { IMAGES_BASE_URL } from '@bridebook/toolbox/src/buildImageUrl';
import { getIsMobile } from '@bridebook/web/app-basic/lib/app/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { useCopyToClipboardWithFeedback } from '@bridebook/web/lib/utils/hooks/use-copy-to-clipboard-with-feedback';
import componentStyles from './author-page-component.style';
import { Button, Text } from '@bridebook/aura';
import { Share } from '@bridebook/aura/src/icons';

interface IProps {
  authorDetails: PostAuthor;
  articles?: IArticle[];
  recordsFound?: number;
  currentPage: number;
}

const AuthorPageComponent = ({ authorDetails, articles, recordsFound, currentPage }: IProps) => {
  const market = useMarket();
  const { t } = useTranslation('articles');
  const isMobile = useSelector(getIsMobile);
  const { isCopied, copy } = useCopyToClipboardWithFeedback();

  const authorNetwork = getAuthorNetworks(authorDetails);

  const handleShare = useCallback(async () => {
    const url = window.location.href;

    if (isMobile && navigator.share) {
      try {
        await navigator.share({ title: document.title, url });
      } catch {
        copy(url);
      }
    } else {
      copy(url);
    }
  }, [isMobile, copy]);

  const styles = componentStyles();

  return (
    <Container pt={6} alignItems="center" gap={10}>
      <Box style={styles.wrapper}>
        <Box style={styles.leftContainer}>
          <Box alignItems="center">
            <img
              src={
                authorDetails?.profile_image?.full
                  ? authorDetails?.profile_image?.full
                  : `${IMAGES_BASE_URL}/assets/article/default-author.jpg`
              }
              style={styles.authorPicture}
            />
            <Box as="h1" style={styles.authorName}>
              {authorDetails?.display_name}
            </Box>
            {authorDetails?.bb_job_title && (
              <Text className="mb-4 text-space-75">{authorDetails.bb_job_title}</Text>
            )}
            <Box gap={2} flexDirection="row">
              <Box gap={2} flexDirection="row">
                {authorNetwork.map((network) => (
                  <A
                    target="_blank"
                    rel="noopener noreferrer"
                    key={`social-sharing-network-${network.name}`}
                    href={network.url}>
                    <Network
                      icon={network.icon}
                      iconWidth={network.iconWidth}
                      color={network.color}
                    />
                  </A>
                ))}
              </Box>
              <Button
                variant="secondary"
                size="sm"
                iconRight={isCopied ? undefined : Share}
                onClick={handleShare}>
                {isCopied ? t('link.copied') : t('share.button')}
              </Button>
            </Box>
          </Box>
        </Box>
        <Box style={styles.rightContainer}>
          {authorDetails?.description && (
            <div className="whitespace-pre-wrap">{authorDetails.description[market.language]}</div>
          )}
          {!!authorDetails?.bb_years_of_experience && (
            <Text className="mt-4 text-space-75">
              {t('authorPage.yearsOfExperience', {
                amount: authorDetails.bb_years_of_experience,
              })}
            </Text>
          )}
          {authorDetails?.bb_media_appearances && (
            <Text className="mt-4 text-space-75">
              {t('authorPage.asSeenIn')} {authorDetails.bb_media_appearances}
            </Text>
          )}
        </Box>
      </Box>
      <Box style={styles.startPlanningWrapper}>
        <Box>
          <Box style={styles.title}>{t('start.planning.for.free')}</Box>
          <Box style={styles.subTitle}>{t('signup.for.free')}</Box>
        </Box>
        <Box style={styles.signupButton}>
          <SignupLink>
            <ButtonV2 size="small">{t('signup')}</ButtonV2>
          </SignupLink>
        </Box>
      </Box>
      <div className="my-5">
        <AuthorRelatedArticles
          authorName={authorDetails?.display_name}
          articles={articles}
          isMobile={isMobile}
          recordsFound={recordsFound}
          currentPage={currentPage}
        />
      </div>
    </Container>
  );
};

export default AuthorPageComponent;
